import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { DATE_PRESETS } from './paymentUtils';
import { PaymentStatusCards } from './PaymentStatusCards';
import { PaymentFiltersBar } from './PaymentFiltersBar';
import { PaymentsList } from './PaymentsList';
import { PaginationBar } from './PaginationBar';
import { PaymentToolsMenu } from './PaymentToolsMenu';
import { PreviewDialog } from './PreviewDialog';
import { BackfillDialog } from './BackfillDialog';
import { IgnoreDialog } from './IgnoreDialog';
import { IgnoreListDialog } from './IgnoreListDialog';

const STORAGE_KEY = 'billpay-payments-filters';
const defaultFilters = () => ({
  ...DATE_PRESETS[1].range(), preset: '60', status: '', search: '', page: 1, page_size: 50, sort: 'date', dir: 'desc',
});
const loadFilters = () => {
  try {
    return { ...defaultFilters(), ...JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') };
  } catch {
    return defaultFilters();
  }
};

const toParams = (f, withPaging) => {
  const p = new URLSearchParams();
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.search.trim()) p.set('search', f.search.trim());
  if (withPaging) {
    if (f.status) p.set('status', f.status);
    p.set('page', f.page); p.set('page_size', f.page_size); p.set('sort', f.sort); p.set('dir', f.dir);
  }
  return p.toString();
};

export const MercuryPaymentsTab = ({ apiFetch, apiUrl, showMessage, onSyncPayments, syncing, onChanged }) => {
  const [filters, setFilters] = useState(loadFilters);
  const [list, setList] = useState({ payments: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState('');
  const [ignoreTarget, setIgnoreTarget] = useState(null);
  const base = `${apiUrl}/api/billpay/payments`;

  const reload = useCallback(async () => {
    setLoading(true);
    const [l, s] = await Promise.all([
      apiFetch(`${base}/list?${toParams(filters, true)}`), apiFetch(`${base}/stats?${toParams(filters, false)}`),
    ]);
    if (l.ok) setList(await l.json());
    if (s.ok) setStats(await s.json());
    setLoading(false);
  }, [apiFetch, base, filters]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
  }, [filters, reload]);

  const update = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const onSort = (field) => update({ sort: field, dir: filters.sort === field && filters.dir === 'desc' ? 'asc' : 'desc', page: 1 });
  const refreshAll = async () => { await reload(); onChanged?.(); };

  const post = async (url, okMsg, opts = {}) => {
    const resp = await apiFetch(url, { method: 'POST', ...opts });
    const body = await resp.json().catch(() => ({}));
    showMessage(resp.ok ? 'success' : 'error', resp.ok ? okMsg(body) : body.detail || 'Request failed');
    return resp.ok;
  };

  const handlers = {
    onApprove: async (p) => {
      if (!window.confirm(`Post ${p.counterparty_name} payment to QuickBooks?`)) return;
      await post(`${base}/${p.mercury_txn_id}/approve`, (b) => b.message || 'Posted to QBO');
      refreshAll();
    },
    onIgnore: (p) => setIgnoreTarget(p),
    onUnignore: async (p) => { await post(`${base}/${p.mercury_txn_id}/unignore`, () => 'Payment un-ignored'); refreshAll(); },
  };

  const confirmIgnore = async (p, { reason, alwaysIgnore }) => {
    const q = reason ? `?note=${encodeURIComponent(reason)}` : '';
    const ok = await post(`${base}/${p.mercury_txn_id}/ignore${q}`, () => 'Payment ignored');
    if (ok && alwaysIgnore) {
      await post(`${base}/ignore-list`, () => `${p.counterparty_name} added to the ignore list`, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_id: p.counterparty_id, name: p.counterparty_name, recipient_name: p.counterparty_name, reason }),
      });
    }
    setIgnoreTarget(null);
    refreshAll();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border" data-testid="mercury-payments-tab">
      <div className="p-4 sm:p-6 border-b space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900">Mercury Payments</h3>
            <p className="text-sm text-slate-500">Match Mercury payments to QBO bills and sync to QuickBooks</p>
            {stats && (
              <span data-testid="auto-post-status"
                title={stats.auto_post_enabled ? 'Exact matches (bill_<id> attachment or invoice # + vendor) post to QBO automatically every 5 minutes' : 'Matches wait as Proposed until approved'}
                className={`inline-block mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium border ${stats.auto_post_enabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                Auto-post {stats.auto_post_enabled ? 'ON' : 'OFF'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" data-testid="payments-sync-btn" onClick={async () => { await onSyncPayments(); reload(); }}
              disabled={syncing} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="hidden sm:inline">Sync Payments</span>
            </button>
            <PaymentToolsMenu onPreview={() => setDialog('preview')} onBackfill={() => setDialog('backfill')}
              onIgnoreList={() => setDialog('ignore-list')} />
          </div>
        </div>
        <PaymentStatusCards stats={stats} activeStatus={filters.status} onSelect={(status) => update({ status, page: 1 })} />
        <PaymentFiltersBar filters={filters} onChange={setFilters} />
      </div>
      <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        <PaymentsList payments={list.payments} sort={filters.sort} dir={filters.dir} onSort={onSort} handlers={handlers} />
      </div>
      <PaginationBar page={filters.page} pageSize={filters.page_size} total={list.total} onChange={update} />
      <PreviewDialog open={dialog === 'preview'} onClose={() => setDialog('')} apiFetch={apiFetch} apiUrl={apiUrl} />
      <BackfillDialog open={dialog === 'backfill'} onClose={() => setDialog('')} apiFetch={apiFetch} apiUrl={apiUrl} onDone={refreshAll} />
      <IgnoreListDialog open={dialog === 'ignore-list'} onClose={() => setDialog('')} apiFetch={apiFetch} apiUrl={apiUrl} />
      <IgnoreDialog key={ignoreTarget?.mercury_txn_id || "none"} payment={ignoreTarget} onClose={() => setIgnoreTarget(null)} onConfirm={confirmIgnore} />
    </div>
  );
};
